import type { ReactNode } from 'react';
import { cn } from '../../../lib/utils';

interface ReportingPanelProps {
  children: ReactNode;
  className?: string;
}

export function ReportingPanel({ children, className }: ReportingPanelProps) {
  return (
    <div
      className={cn(
        'rounded-xl border border-neutral-800/70 bg-neutral-900/40',
        className,
      )}
    >
      {children}
    </div>
  );
}

interface ReportingInsetProps {
  children: ReactNode;
  className?: string;
}

export function ReportingInset({ children, className }: ReportingInsetProps) {
  return (
    <div
      className={cn(
        'rounded-lg border border-neutral-800/70 bg-neutral-950/40',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function LifetimeTag() {
  return (
    <span
      className="rounded-md border border-neutral-800 bg-neutral-900/60 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-neutral-500"
      title="Cumulative live store totals — not scoped to the selected date range"
    >
      Lifetime
    </span>
  );
}

interface ReportingStatusPillProps {
  label: ReactNode;
  tone?: 'teal' | 'neutral' | 'amber' | 'rose';
  className?: string;
}

const pillTones: Record<
  NonNullable<ReportingStatusPillProps['tone']>,
  string
> = {
  teal: 'border-teal-500/20 bg-teal-500/[0.08] text-teal-300',
  amber: 'border-amber-500/20 bg-amber-500/[0.08] text-amber-300',
  rose: 'border-rose-500/20 bg-rose-500/[0.08] text-rose-300',
  neutral: 'border-neutral-700/60 bg-neutral-800/50 text-neutral-300',
};

export function ReportingStatusPill({
  label,
  tone = 'neutral',
  className,
}: ReportingStatusPillProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md border px-1.5 py-0.5 text-[11px] font-medium',
        pillTones[tone],
        className,
      )}
    >
      {label}
    </span>
  );
}

interface ReportingSectionHeaderProps {
  title: string;
  subtitle?: string;
  trailing?: ReactNode;
  className?: string;
}

export function ReportingSectionHeader({
  title,
  subtitle,
  trailing,
  className,
}: ReportingSectionHeaderProps) {
  return (
    <div
      className={cn(
        'flex flex-col gap-1.5 sm:flex-row sm:items-baseline sm:justify-between',
        className,
      )}
    >
      <div className="min-w-0">
        <h3 className="text-sm font-medium text-neutral-200">{title}</h3>
        {subtitle && (
          <p className="mt-0.5 text-xs leading-relaxed text-neutral-500">
            {subtitle}
          </p>
        )}
      </div>
      {trailing && (
        <div className="flex shrink-0 items-center gap-2 text-xs text-neutral-500">
          {trailing}
        </div>
      )}
    </div>
  );
}

interface RatingStarsProps {
  value: number | null;
  size?: 'sm' | 'md';
  showValue?: boolean;
  fallback?: string;
  className?: string;
}

export function RatingStars({
  value,
  size = 'sm',
  showValue = false,
  fallback = '—',
  className,
}: RatingStarsProps) {
  const dimensions = size === 'md' ? 'h-3.5 w-3.5' : 'h-3 w-3';

  if (value === null || Number.isNaN(value)) {
    return (
      <span className={cn('text-xs text-neutral-600', className)}>
        {fallback}
      </span>
    );
  }

  const clamped = Math.max(0, Math.min(5, value));

  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      <span className="inline-flex">
        {[0, 1, 2, 3, 4].map((index) => {
          const fill = Math.max(0, Math.min(1, clamped - index));
          return (
            <span
              key={index}
              className={cn('relative inline-block', dimensions)}
              aria-hidden="true"
            >
              <StarSvg className="absolute inset-0 fill-neutral-700" />
              <span
                className="absolute inset-y-0 left-0 overflow-hidden"
                style={{ width: `${fill * 100}%` }}
              >
                <StarSvg className={cn(dimensions, 'fill-amber-300')} />
              </span>
            </span>
          );
        })}
      </span>
      {showValue && (
        <span className="text-xs font-medium tabular-nums text-neutral-300">
          {clamped.toFixed(2)}
        </span>
      )}
    </span>
  );
}

function StarSvg({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 20 20"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <path d="M10 1.6l2.6 5.27 5.82.85-4.21 4.1.99 5.79L10 14.88 4.8 17.6l.99-5.79L1.58 7.72l5.82-.85L10 1.6z" />
    </svg>
  );
}

interface RatingDistributionBarProps {
  buckets: {
    one: number | null;
    two: number | null;
    three: number | null;
    four: number | null;
    five: number | null;
  };
  variant?: 'compact' | 'detailed';
  className?: string;
}

const RATING_TONES: Record<
  'one' | 'two' | 'three' | 'four' | 'five',
  { bar: string; label: string }
> = {
  one: { bar: 'bg-rose-500/70', label: '1★' },
  two: { bar: 'bg-rose-400/60', label: '2★' },
  three: { bar: 'bg-amber-400/60', label: '3★' },
  four: { bar: 'bg-teal-400/60', label: '4★' },
  five: { bar: 'bg-teal-300', label: '5★' },
};

export function RatingDistributionBar({
  buckets,
  variant = 'compact',
  className,
}: RatingDistributionBarProps) {
  const safeBuckets = {
    one: buckets.one ?? 0,
    two: buckets.two ?? 0,
    three: buckets.three ?? 0,
    four: buckets.four ?? 0,
    five: buckets.five ?? 0,
  };
  const total =
    safeBuckets.one +
    safeBuckets.two +
    safeBuckets.three +
    safeBuckets.four +
    safeBuckets.five;

  if (total === 0) {
    return (
      <span className={cn('text-xs text-neutral-600', className)}>
        No reviews
      </span>
    );
  }

  const order = ['five', 'four', 'three', 'two', 'one'] as const;

  if (variant === 'compact') {
    return (
      <div
        className={cn(
          'flex h-1 w-full overflow-hidden rounded-full bg-neutral-800/60',
          className,
        )}
      >
        {order.map((key) => {
          const value = safeBuckets[key];
          if (value === 0) return null;
          const pct = (value / total) * 100;
          return (
            <span
              key={key}
              className={RATING_TONES[key].bar}
              style={{ width: `${pct}%` }}
              title={`${RATING_TONES[key].label}: ${value.toLocaleString()}`}
            />
          );
        })}
      </div>
    );
  }

  return (
    <div className={cn('space-y-1.5', className)}>
      {order.map((key) => {
        const value = safeBuckets[key];
        const pct = total === 0 ? 0 : (value / total) * 100;
        return (
          <div key={key} className="flex items-center gap-2.5">
            <span className="w-7 text-[11px] tabular-nums text-neutral-500">
              {RATING_TONES[key].label}
            </span>
            <div className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-neutral-800/60">
              <span
                className={cn(
                  'absolute inset-y-0 left-0 rounded-full',
                  RATING_TONES[key].bar,
                )}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="w-12 text-right text-[11px] tabular-nums text-neutral-500">
              {value.toLocaleString()}
            </span>
          </div>
        );
      })}
    </div>
  );
}

interface RatioBarProps {
  numerator: number;
  denominator: number;
  numeratorLabel: string;
  denominatorLabel: string;
  className?: string;
}

export function RatioBar({
  numerator,
  denominator,
  numeratorLabel,
  denominatorLabel,
  className,
}: RatioBarProps) {
  const safeDenominator = Math.max(denominator, 0);
  const pct =
    safeDenominator === 0
      ? 0
      : Math.min(100, Math.round((numerator / safeDenominator) * 100));

  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="flex items-center justify-between text-[11px] text-neutral-500">
        <span>
          <span className="text-neutral-300">{numeratorLabel}</span>
          <span className="ml-1 tabular-nums">
            {numerator.toLocaleString()}
          </span>
        </span>
        <span>
          <span className="text-neutral-400">{denominatorLabel}</span>
          <span className="ml-1 tabular-nums">
            {safeDenominator.toLocaleString()}
          </span>
        </span>
      </div>
      <div className="relative h-1 w-full overflow-hidden rounded-full bg-neutral-800/60">
        <span
          className="absolute inset-y-0 left-0 rounded-full bg-teal-400/80"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

interface SparkbarProps {
  values: number[];
  className?: string;
  ariaLabel?: string;
}

export function Sparkbar({ values, className, ariaLabel }: SparkbarProps) {
  if (values.length === 0) {
    return null;
  }

  const max = Math.max(...values, 1);

  return (
    <div
      className={cn('flex h-6 items-end gap-[2px]', className)}
      role="img"
      aria-label={ariaLabel}
    >
      {values.map((value, index) => {
        const height = (value / max) * 100;
        return (
          <span
            key={index}
            className="flex-1 rounded-sm bg-teal-400/40"
            style={{
              height: `${Math.max(8, height)}%`,
              opacity: value === 0 ? 0.2 : 1,
            }}
          />
        );
      })}
    </div>
  );
}
